import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react/dist/csr/ClockCounterClockwise';
import { CardHead } from '../../design-system/composites/Card/Card';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';

import { FunnelIcon } from '@phosphor-icons/react/dist/csr/Funnel';
import { XCircleIcon } from '@phosphor-icons/react/dist/csr/XCircle';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import type { ListStatus } from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { AuditEntryTable } from '../audit/AuditEntryTable';
import styles from './MyActivity.module.css';
import { ApiError, api, type AuditEntry, type AuditQuery } from '../../lib/api/client';

/**
 * The caller's own rows from `GET /profile/activity` (`api.myActivity`) —
 * task 8's counterpart to `AuditLogScreen` (task 7), reusing `AuditEntryTable`
 * UNCHANGED with `hideActorColumn` set: every row's actor is the person
 * reading this screen, so an Actor column would repeat the same name down
 * the page. Self-contained the same way `SessionList` is (that component's
 * own docblock) — fetches on its own mount, owns its own load status, and
 * `SettingsScreen` hands it nothing and reads nothing back.
 *
 * ## No export control — decision, not oversight
 *
 * Export is an administrator action: V-5 caps it and audits it as its own
 * `audit.exported` entry (`AuditLogScreen.tsx`'s own docblock), and
 * `api.auditLogExportUrl` is never imported here. That link stays exactly
 * where `AuditLogScreen` renders it, and nowhere else.
 *
 * ## Filters: THREE, and every one of them is enforced by the server
 *
 * This section shipped with none, on the reasoning that a person reading
 * their own activity already knows who did it. True, and beside the point —
 * they still cannot find last Tuesday's case edit in a cursor-paginated
 * trail without one.
 *
 * `from`, `to` and `action` are exactly the three `ActivityController::
 * index()` validates and applies to its own query. `actor_user_id`,
 * `entity_type` and the rest of `AuditQuery` are NOT offered, because that
 * controller reads none of them: sending one is silently inert, and a
 * control that appears to narrow a list and does nothing is worse than no
 * control. `api.myActivity` strips `actor_user_id` in its own signature for
 * the same reason.
 *
 * NOT A CLIENT-SIDE FILTER OVER THE LOADED PAGE. Ten rows filtered in the
 * browser while presenting itself as a search of the trail would answer "you
 * did nothing that day" about a day the reader worked. Every filter here is
 * a query parameter, and changing one resets to the first page — a cursor
 * minted against one filter set is meaningless against another
 * (`useCursorPages`' own docblock).
 *
 * ## RULING (the brief asks this be decided and documented): this section
 * PAGINATES — the same verbatim cursor round-trip `AuditLogScreen` (task 7)
 * already uses, not a capped "recent activity" list.
 *
 * `GET /profile/activity` is the same cursor-paginated endpoint `GET
 * /audit-log` is. Capping this panel at one page and presenting it as
 * "activity" would silently tell a person their history is shorter than it
 * really is — precisely the misleading shape the brief warns a silent cap
 * produces. `meta.next_cursor` is read out of one response and handed back
 * verbatim as `cursor` on the next `api.myActivity` call, via
 * `useCursorPages` (M-19 task 2) — never parsed, inspected or constructed,
 * the identical discipline `AuditLogScreen.tsx`'s own note states.
 */

export function MyActivity() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly AuditEntry[]>([]);
  const [reference, setReference] = useState<string | null>(null);

  /*
   * TWO copies of the filter state, the same split every other filtered list
   * screen in this application uses. `draft` is what the fields hold while
   * someone types; `applied` is what the last submit committed and what the
   * fetch reads. One copy would refetch on every keystroke.
   */
  const [draft, setDraft] = useState({ action: '', from: '', to: '' });
  const [applied, setApplied] = useState({ action: '', from: '', to: '' });
  // 50 — `ActivityController::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;

  /**
   * C-2's fix — see `AuditLogScreen.tsx`'s identical note. This screen has
   * no filters, but `retry()` can still fire a second `load` while the
   * effect's own call (a page turn) is still in flight, and a late response
   * to an earlier page must not overwrite a later one that already landed.
   * A monotonic counter, not a single `cancelled` flag, because more than
   * one call can be in flight across `retry()` and the paging effect.
   */
  const requestSeq = useRef(0);

  const load = useCallback(async (query: AuditQuery) => {
    const seq = ++requestSeq.current;
    // Whether THIS request carried a filter — read off the query rather than
    // off `applied`, so a response cannot be classified against a filter set
    // that changed while it was in flight.
    const filtered = query.action !== undefined || query.from !== undefined || query.to !== undefined;
    setStatus('loading');
    setReference(null);

    try {
      const page = await api.myActivity(query);
      if (seq !== requestSeq.current) return; // superseded — drop this response
      setRows(page.data);
      // Read out of THIS response and nowhere else — see the component
      // docblock's ruling on the cursor never being parsed, inspected or
      // constructed. `AuditPage.meta` carries no `total` — see the
      // `<Pagination total={null}>` calls below.
      observe({ next_cursor: page.meta.next_cursor, total: null });
      setStatus(page.data.length === 0 ? (filtered ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return; // superseded — drop this response
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  /**
   * The applied filters as query parameters, empty strings dropped.
   *
   * An empty `action` must be ABSENT, not `action: ''` — the server validates
   * it with `Rule::in(AuditAction::ALL)`, so an empty string is a 422 rather
   * than "no filter".
   */
  const filterQuery = useCallback((): AuditQuery => {
    const query: AuditQuery = {};
    if (applied.action !== '') query.action = applied.action;
    if (applied.from !== '') query.from = applied.from;
    if (applied.to !== '') query.to = applied.to;
    return query;
  }, [applied]);

  useEffect(() => {
    void load({
      ...filterQuery(),
      per_page: pages.perPage,
      ...(pages.cursor === null ? {} : { cursor: pages.cursor }),
    });
  }, [load, filterQuery, pages.cursor, pages.perPage]);

  function retry(): void {
    void load({
      ...filterQuery(),
      per_page: pages.perPage,
      ...(pages.cursor === null ? {} : { cursor: pages.cursor }),
    });
  }

  const goToFirstPage = pages.first;

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setApplied(draft);
    // A cursor minted against the previous filter set addresses a row that
    // may not be in the new one at all — `useCursorPages` throws the stack
    // away for exactly this, and it is the caller's job to say when.
    goToFirstPage();
  }

  function handleClearFilters(): void {
    const empty = { action: '', from: '', to: '' };
    setDraft(empty);
    setApplied(empty);
    goToFirstPage();
  }

  function updateDraft(field: 'action' | 'from' | 'to') {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraft((current) => ({ ...current, [field]: value }));
    };
  }

  return (
    <section className={styles['panel']} aria-labelledby="myActivitySectionTitle">
      <CardHead
        id="myActivitySectionTitle"
        icon={<ClockCounterClockwiseIcon size={16} weight="duotone" />}
        title={t('myActivity.title')}
      />

      <form className={styles['filters']} onSubmit={handleFilterSubmit}>
        <Input
          label={t('audit.filterAction')}
          width="phone"
          value={draft.action}
          onChange={updateDraft('action')}
        />
        <Input
          label={t('audit.filterFrom')}
          width="compact"
          type="date"
          value={draft.from}
          onChange={updateDraft('from')}
        />
        <Input
          label={t('audit.filterTo')}
          width="compact"
          type="date"
          value={draft.to}
          onChange={updateDraft('to')}
        />

        <div className={styles['filterActions']}>
          <Button type="submit" icon={<FunnelIcon size={16} />}>
            {t('actions.filter')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClearFilters}
            icon={<XCircleIcon size={16} />}
          >
            {t('actions.clearFilters')}
          </Button>
        </div>
      </form>

      <AuditEntryTable
        caption={t('myActivity.title')}
        rows={rows}
        status={status}
        error={{ title: t('myActivity.loadFailed') }}
        empty={{ body: t('myActivity.empty') }}
        /*
         * `no-results`, not `empty`, once a filter is applied: the two have
         * different recovery copy, and telling someone their activity trail
         * is empty when a date filter is hiding it is how a person concludes
         * their history was lost.
         */
        noResults={{ title: t('list.noResultsTitle') }}
        onClearFilters={handleClearFilters}
        hideActorColumn
      />

      <Pagination pages={pages} total={null} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button
            icon={<ArrowClockwiseIcon size={16} />}
            variant="secondary"
            onClick={() => {
              retry();
            }}
          >
            {t('myActivity.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="myActivityRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}
    </section>
  );
}
